import { useContext, useEffect, useState, useRef } from "react";
import * as sdk from "matrix-js-sdk";
import { API_URL } from "src/scenes/App";
import { UserContext } from "src/scenes/App/UserContext";
import bootstrapOlm from "../../Olmbootstrapper";
import { Buffer } from "buffer";
import { IndexedDBCryptoStore, ClientEvent, RoomEvent, EventStatus, MatrixEventEvent, SyncState } from "matrix-js-sdk";
import { KnownMembership } from "matrix-js-sdk/lib/@types/membership";
import { ADMIN_MATRIX_ID } from "src/constants/chat";
import MatrixChat from "src/scenes/Chat/models/MatrixChat";
import {
    getMatrixClientSession,
    prepareJoinedRoomsForEncryption,
    prepareRoomEncryptionForReceive,
    setMatrixClientSession as setExternalMatrixClientSession,
} from "../../utils";
import { deriveRecoveryKeyFromPassphrase } from "matrix-js-sdk/lib/crypto-api/key-passphrase";
import {
    SecurePassphraseStorage,
    attemptChatBackupWithLoginPassphrase,
    CHAT_BACKUP_SETUP_RESULT,
    prepareUserCryptoInBackground
} from "../../utils";
import { fullMatrixLogout } from "../../utils";
import {
    clearMatrixRecoveryPending,
    completeMatrixBindingMigration,
    markMatrixBindingUnusable,
} from "../../utils/matrixStorage";
import {
    confirmMatrixDeviceKeysUploaded,
    initializeMatrixEncryptionReadiness,
    migrateMatrixInstallationOnLogin,
    MatrixCryptoInitializationError,
    registerMatrixClient,
    reportMatrixRegistrationReady,
    resetMatrixInstallation,
} from "../../utils/matrixRegistration";
import {
    cancelRoomKeyRequest,
    isPermanentlyWithheldRoomKey,
    isRetryableMissingRoomKey,
    MATRIX_KEY_RECOVERY_RETRY_DELAYS_MS,
    MATRIX_KEY_RECOVERY_STATE,
    requestRoomKey,
    retryExistingEncryptedEvents,
    setMatrixKeyRecoveryState,
} from "../../utils/matrixKeyRecovery";
import {
    CALL_ANSWER,
    CALL_HANGUP,
    CALL_INVITE,
    CALL_NOTE,
    CALL_REJECT
} from "src/constants/matrixEventTypes";

import {
    checkCryptoStoreGeneration,
    createMatrixGuardedFetch,
    MatrixContinuityError,
    MATRIX_RECOVERY_MESSAGE,
} from "../../utils/matrixContinuity";
import {
    createMatrixSyncStore,
    createSlidingSync,
    expandSlidingSyncRoomList,
    getStoredMatrixSyncMode,
    MATRIX_SYNC_MODE,
    resolveMatrixSyncMode,
    storeMatrixSyncMode,
} from "../../utils/matrixSync";
import { canUseVideoCalling } from "src/utils/checkedInUser";

window.Buffer = Buffer;

const CALL_EVENT_TYPES = [
    CALL_INVITE,
    CALL_ANSWER,
    CALL_HANGUP,
    CALL_NOTE,
    CALL_REJECT
];

export const useMatrixClient = (headers, options) => {
    const enabled = options?.enabled !== undefined ? options.enabled : true;
    const userContext = useContext(UserContext);
    const isInitializing = useRef(false);
    const hasHandledUnauthorizedSync = useRef(false);
    const hasAttemptedAutomaticMigration = useRef(false);
    const initRetryCount = useRef(0);
    const loginPassphraseRef = useRef("");
    const onLoginPassphraseConsumedRef = useRef(null);
    const [matrixClient, setMatrixClient] = useState(null);
    const [isInitialSyncComplete, setIsInitialSyncComplete] = useState(false);
    const [matrixRecoveryRequired, setMatrixRecoveryRequired] = useState(false);
    const [chatBackupRecoveryRequired, setChatBackupRecoveryRequired] = useState(false);
    const authorizationHeader = headers?.headers?.Authorization;

    loginPassphraseRef.current = options?.loginPassphrase || "";
    onLoginPassphraseConsumedRef.current = options?.onLoginPassphraseConsumed || null;

    const syncMatrixClient = (client) => {
        setMatrixClient(client);
        setExternalMatrixClientSession(client);
    };

    const consumeLoginPassphrase = () => {
        const passphrase = loginPassphraseRef.current;
        loginPassphraseRef.current = "";
        if (passphrase && onLoginPassphraseConsumedRef.current) {
            onLoginPassphraseConsumedRef.current();
        }
        return passphrase;
    };

    const attemptLoginPassphraseChatBackup = async (clientToInitialize) => {
        const passphrase = consumeLoginPassphrase();
        if (!passphrase) {
            return;
        }

        try {
            const result = await attemptChatBackupWithLoginPassphrase(clientToInitialize, passphrase);
            if (result !== CHAT_BACKUP_SETUP_RESULT.SKIPPED) {
                clearMatrixRecoveryPending(userContext.uuid);
                setChatBackupRecoveryRequired(false);
            }
            console.log("[Matrix] Login password chat backup attempt completed:", result);
        } catch (error) {
            console.warn("[Matrix] Login password chat backup attempt failed. Manual recovery remains available.", error);
            try {
                const backupInfo = await clientToInitialize.getCrypto?.()?.getKeyBackupInfo?.();
                setChatBackupRecoveryRequired(!!backupInfo?.auth_data);
            } catch (backupInfoError) {
                console.warn("[Matrix] Could not confirm whether manual Chat backup recovery is available.", backupInfoError);
            }
        }
    };

    const acknowledgeChatBackupRecovery = () => {
        setChatBackupRecoveryRequired(false);
    };

    const continueChatSecuritySetup = async (clientToInitialize) => {
        try {
            await prepareJoinedRoomsForEncryption(clientToInitialize);
            const storedPassphrase = SecurePassphraseStorage.get(clientToInitialize);
            if (storedPassphrase && !loginPassphraseRef.current) {
                await prepareUserCryptoInBackground(clientToInitialize, storedPassphrase);
            }
            await attemptLoginPassphraseChatBackup(clientToInitialize);
            await retryExistingEncryptedEvents(clientToInitialize, (event, error) => {
                if (isRetryableMissingRoomKey(error, event)) {
                    console.debug("[Matrix] An encrypted event still requires live-device recovery after backup restore.");
                }
            });
        } catch (error) {
            console.warn("[Matrix] Background chat security setup failed. Manual recovery remains available.", error);
        }
    };

    useEffect(() => {
        if (!enabled || !authorizationHeader) {
            return;
        }

        if (isInitializing.current) return;
        isInitializing.current = true;
        
        let createdMatrixClient = null;
        let retryTimer = null;

        const initializeMatrixClient = async () => {
            try {
                setMatrixRecoveryRequired(false);
                let registration;
                try {
                    registration = await registerMatrixClient(headers, userContext);
                } catch (registrationError) {
                    if (!isMatrixContinuityFailure(registrationError) ||
                        !loginPassphraseRef.current ||
                        hasAttemptedAutomaticMigration.current) {
                        throw registrationError;
                    }

                    hasAttemptedAutomaticMigration.current = true;
                    console.warn("[Matrix] Migrating an incompatible legacy device during login.");
                    registration = await migrateMatrixInstallationOnLogin(headers, userContext);
                }
                const {
                    installationId,
                    session,
                    cryptoStoreGeneration,
                    cryptoDatabasePrefix,
                    cryptoStoreMigrationPending,
                } = registration;
                const readinessIdentifiers = {
                    installationId,
                    cryptoStoreGeneration,
                    deviceId: session.deviceId,
                };
                const currentClient = getMatrixClientSession();

                if (currentClient && currentClient.getUserId?.() === session.userId && currentClient.getDeviceId?.() === session.deviceId) {
                    createdMatrixClient = currentClient;
                    if (cryptoStoreMigrationPending) {
                        await checkCryptoStoreGeneration(cryptoDatabasePrefix, cryptoStoreGeneration, true);
                        completeMatrixBindingMigration(
                            userContext.uuid,
                            session,
                            cryptoStoreGeneration,
                            cryptoDatabasePrefix
                        );
                    }
                    installSyncAuthRecovery(currentClient);
                    syncMatrixClient(currentClient);
                    setIsInitialSyncComplete(true);
                    initRetryCount.current = 0;
                    try {
                        await confirmMatrixDeviceKeysUploaded(currentClient, null);
                        await reportMatrixRegistrationReady(headers, readinessIdentifiers, null);
                    } catch (readinessError) {
                        console.error("[Matrix] Failed to report encryption readiness:", readinessError);
                    }
                    void continueChatSecuritySetup(currentClient);
                    return;
                }

                const createMatrixClientInstance = () => {
                    const cryptoStore = new IndexedDBCryptoStore(
                        window.indexedDB,
                        `matrix_crypto_store_${session.userId}_${session.deviceId}`
                    );
                    const syncStore = createMatrixSyncStore(session.userId, session.deviceId);
                    const matrixClientInstance = createClient(
                        API_URL,
                        session.token,
                        session.userId,
                        session.deviceId,
                        cryptoStore,
                        syncStore,
                        createMatrixGuardedFetch(window.fetch.bind(window), () => {
                            try {
                                markMatrixBindingUnusable(userContext.uuid, session.deviceId);
                            } finally {
                                stopFailedMatrixClient(createdMatrixClient);
                                syncMatrixClient(null);
                                setIsInitialSyncComplete(false);
                                console.error("[Matrix] Crypto continuity failure", {
                                    userId: session.userId, deviceId: session.deviceId,
                                    endpoint: "/keys/upload", status: 400, code: "M_UNKNOWN",
                                });
                            }
                        }),
                        !canUseVideoCalling(userContext),
                    );
                    return { matrixClientInstance, syncStore };
                };
                const { matrixClientInstance, syncStore } = createMatrixClientInstance();
                createdMatrixClient = matrixClientInstance;
                installSyncAuthRecovery(matrixClientInstance);

                try {
                    await syncStore.startup();
                } catch (syncStoreError) {
                    console.warn("[Matrix] Room cache unavailable; continuing with in-memory sync state.", syncStoreError);
                }

                await initializeOlm();
                if (!registration.isNew && !cryptoStoreMigrationPending) {
                    await checkCryptoStoreGeneration(cryptoDatabasePrefix, cryptoStoreGeneration, false);
                }
                const initializeEncryption = async (clientToInitialize) => {
                    await initializeMatrixEncryptionReadiness(
                        clientToInitialize,
                        cryptoDatabasePrefix,
                        async () => {
                            await checkCryptoStoreGeneration(
                                cryptoDatabasePrefix,
                                cryptoStoreGeneration,
                                registration.isNew || cryptoStoreMigrationPending
                            );
                            if (cryptoStoreMigrationPending) {
                                completeMatrixBindingMigration(
                                    userContext.uuid,
                                    session,
                                    cryptoStoreGeneration,
                                    cryptoDatabasePrefix
                                );
                            }
                            const syncMode = resolveMatrixSyncMode({
                                isNewInstallation: registration.isNew,
                                storedMode: getStoredMatrixSyncMode(session.userId, session.deviceId),
                            });
                            storeMatrixSyncMode(session.userId, session.deviceId, syncMode);
                            const slidingSync = syncMode === MATRIX_SYNC_MODE.SLIDING
                                ? createSlidingSync(clientToInitialize, API_URL)
                                : null;
                            clientToInitialize.__dailyfixmeSyncMode = syncMode;
                            const initialSyncPromise = initialSync(
                                clientToInitialize,
                                false,
                                syncMode === MATRIX_SYNC_MODE.SLIDING
                            );
                            const initialSyncStartedAt = window.performance.now();
                            await clientToInitialize.startClient({
                                initialSyncLimit: 1,
                                disablePresence: false,
                                ...(slidingSync ? { slidingSync } : {}),
                            });
                            syncMatrixClient(clientToInitialize);
                            try {
                                await initialSyncPromise;
                            } catch (syncError) {
                                storeMatrixSyncMode(session.userId, session.deviceId, MATRIX_SYNC_MODE.LEGACY);
                                console.warn("[Matrix] Sliding Sync unavailable; retrying with legacy sync.", syncError);
                                throw syncError;
                            }
                            if (slidingSync) {
                                expandSlidingSyncRoomList(slidingSync);
                            }
                            console.info("[Matrix] Initial sync complete.", {
                                durationMs: Math.round(window.performance.now() - initialSyncStartedAt),
                                mode: syncMode,
                            });
                            setIsInitialSyncComplete(true);
                        },
                        headers,
                        readinessIdentifiers,
                        null
                    );
                    initRetryCount.current = 0;
                    void continueChatSecuritySetup(clientToInitialize);
                };

                await initializeEncryption(matrixClientInstance);

            } catch (error) {
                isInitializing.current = false;
                if (createdMatrixClient) {
                    stopFailedMatrixClient(createdMatrixClient);
                }
                syncMatrixClient(null);
                setIsInitialSyncComplete(false);
                const continuityFailure = isMatrixContinuityFailure(error);
                const cryptoInitializationFailure = error instanceof MatrixCryptoInitializationError;
                const retryDelay = [1000, 2000][initRetryCount.current];

                if (cryptoInitializationFailure && retryDelay) {
                    console.warn("[Matrix] Encryption initialization failed; retrying before replacing the device.", error);
                    initRetryCount.current += 1;
                    retryTimer = window.setTimeout(() => {
                        isInitializing.current = true;
                        initializeMatrixClient();
                    }, retryDelay);
                    return;
                }

                if (cryptoInitializationFailure && loginPassphraseRef.current &&
                    !hasAttemptedAutomaticMigration.current) {
                    hasAttemptedAutomaticMigration.current = true;
                    try {
                        console.warn("[Matrix] Replacing a legacy device whose crypto store could not initialize.");
                        await resetMatrixInstallation(headers, userContext?.uuid);
                        createdMatrixClient = null;
                        isInitializing.current = true;
                        await initializeMatrixClient();
                        return;
                    } catch (migrationError) {
                        console.error("[Matrix] Automatic legacy-device migration failed:", migrationError);
                    }
                }

                if (continuityFailure || cryptoInitializationFailure) {
                    consumeLoginPassphrase();
                    setMatrixRecoveryRequired(true);
                    console.error("[Matrix]", MATRIX_RECOVERY_MESSAGE);
                    return;
                }
                console.error("[Matrix] Initialization failed.");
                if (retryDelay) {
                    initRetryCount.current += 1;
                    retryTimer = window.setTimeout(() => {
                        isInitializing.current = true;
                        initializeMatrixClient();
                    }, retryDelay);
                } else {
                    consumeLoginPassphrase();
                }
            }
        };

        const installSyncAuthRecovery = (clientToMonitor) => {
            if (clientToMonitor.__dailyfixmeMatrixAuthRecoveryInstalled) return;

            clientToMonitor.__dailyfixmeMatrixAuthRecoveryInstalled = true;
            clientToMonitor.on(ClientEvent.Sync, async (state, prevState, data) => {
                if (state !== SyncState.Error && state !== SyncState.Reconnecting) return;
                if (!isUnauthorizedMatrixError(data?.error)) return;
                if (hasHandledUnauthorizedSync.current) return;

                hasHandledUnauthorizedSync.current = true;
                console.warn("[Matrix] Unauthorized sync detected. Resetting Matrix client and refreshing chat session.");
                await fullMatrixLogout();
                await userContext?.appFns?.reinitialiseUser?.();
                window.location.reload();
            });
        };

        initializeMatrixClient();

        return () => {
            if (retryTimer) {
                window.clearTimeout(retryTimer);
            }

            if (createdMatrixClient) {
                console.log("Keeping Matrix client running for background sync.");
            } else {
                console.log("Matrix client is not set, no cleanup needed");
            }
        };
    }, [enabled, authorizationHeader]);

    const initialSync = (client, markReadyWhenPrepared, rejectUnavailableSlidingSync) => {
        return new Promise((resolve, reject) => {
            const handleSync = (state, previousState, data) => {
                if (state === SyncState.Prepared) {
                    client.off(ClientEvent.Sync, handleSync);
                    if (markReadyWhenPrepared) {
                        setIsInitialSyncComplete(true);
                    }
                    resolve();
                    return;
                }
                if (rejectUnavailableSlidingSync && state === SyncState.Error && isUnavailableSyncError(data?.error)) {
                    client.off(ClientEvent.Sync, handleSync);
                    reject(data.error);
                }
            };
            client.on(ClientEvent.Sync, handleSync);
        });
    };

    const runListener = (client, removeRoomInMatrixChatList, upsertRoomInMatrixChatList, upsertRoomTimeline, placedRoomToIndexZeroOfMatrixChatList, openedMatrixChat, setOpenedMatrixChat) => {
        try {
            if (client.__dailyfixmeChatListenerCleanup) {
                client.__dailyfixmeChatListenerCleanup();
            }

            const cleanupFns = [];
            const decryptionRecoveries = new Map();
            const preparedRoomIds = new Set();
            const roomPreparationRefreshPending = new Set();
            const roomPreparationPromises = new Map();
            let fullBackupRestorePromise = null;
            let listenerIsActive = true;
            const addClientListener = (eventName, handler) => {
                client.on(eventName, handler);
                cleanupFns.push(() => client.off(eventName, handler));
            };
            const prepareRoomForReceiving = (room, forceRefresh = false) => {
                const roomId = room?.roomId;
                if (!roomId) {
                    return Promise.resolve(false);
                }
                if (roomPreparationPromises.has(roomId)) {
                    if (forceRefresh) {
                        roomPreparationRefreshPending.add(roomId);
                    }
                    return roomPreparationPromises.get(roomId);
                }
                if (!forceRefresh && preparedRoomIds.has(roomId)) {
                    return Promise.resolve(true);
                }
                const preparation = prepareRoomEncryptionForReceive(client, room).then(prepared => {
                    if (prepared) {
                        preparedRoomIds.add(roomId);
                    }
                    return prepared;
                }).finally(() => {
                    if (roomPreparationPromises.get(roomId) === preparation) {
                        roomPreparationPromises.delete(roomId);
                    }
                    if (listenerIsActive && roomPreparationRefreshPending.delete(roomId)) {
                        prepareRoomForReceiving(room, true);
                    }
                });
                roomPreparationPromises.set(roomId, preparation);
                return preparation;
            };

            /**
             * Fires whenever a new Room is added. This will fire when you are invited to a
             * room, as well as when you join a room. <strong>This event is experimental and
             * may change.</strong>
             * @param room - The newly created, fully populated room.
             */
            addClientListener("Room", function(room) {
                const roomId = room.roomId;
                const latestRoomEvent = room.getLastLiveEvent();
                console.warn("A new Room is added: ", roomId);
                prepareRoomForReceiving(room);
                if (!latestRoomEvent) {
                    upsertRoomInMatrixChatList(roomId);
                    return;
                }
                addToMatrixChatList(latestRoomEvent);
            });

            addClientListener(RoomEvent.MyMembership, (room, membership) => {
                const roomId = room?.roomId;
                if (!roomId) {
                    return;
                }

                if (membership === KnownMembership.Join) {
                    console.warn(`[Room.myMembership] Rejoined room ${roomId}`);
                    prepareRoomForReceiving(room, true);
                    upsertRoomInMatrixChatList(roomId);
                    return;
                }

                if (membership === KnownMembership.Leave) {
                    stopRecoveriesForRoom(roomId);
                    closeChatMessageContainerForDeletedRoom(roomId);
                    removeRoomInMatrixChatList(roomId);
                }
            });

            /**
             * Fires whenever a Room is removed. This will fire when you forget a room.
             * <strong>This event is experimental and may change.</strong>
             * @param roomId - The deleted room ID.
             */
            addClientListener("deleteRoom", function(roomId) {
                console.warn("A `deleteRoom` event triggered for this roomId: ", roomId);
                stopRecoveriesForRoom(roomId);
                removeRoomInMatrixChatList(roomId);
                closeChatMessageContainerForDeletedRoom(roomId);

                const room = client.getRoom(roomId);
                if (room) {
                    room.getLiveTimeline().getEvents().length = 0;
                    room.resetLiveTimeline(); 
                    console.log(`[Matrix] Timeline cleared for deleted room: ${roomId}`);
                }
            });
            
            addClientListener("Room.timeline", (event, room) => {
                /**
                 * Room.timeline event order when initializing a room
                 * 1. m.room.create
                 * 2. m.room.member
                 * 3. m.room.power_levels
                 * 4. m.room.join_rules
                 * 5. m.room.history_visibility
                 * 6. m.room.encryption
                 */

                console.warn("[Room.timeline][" + event.getType() + "]");
                if (event.status === EventStatus.SENDING) {
                    upsertRoomTimeline(event);
                }
                /*
                if (event.getType() === "m.room.encryption") {
                    const algorithm = event.getContent().algorithm;
                    console.warn(`Room ${room.roomId} is encrypted using ${algorithm}`);
                    // Encryption has been enabled (or the room is already encrypted)
                    //updateMatrixChatListForThisRoom(room.roomId);
                } else if (event.getType() === "m.room.member") {
                    const membership = event.getContent().membership;
                    const userId = event.getStateKey();
                    if (event.isEncrypted()) { // Check if room is encrypted before assuming it is.
                        console.warn(`User ${userId} ${membership}ed encrypted room ${room.roomId}. Key exchange will occur automatically.`);
                    }
                } */
            });

            const addToMatrixChatList = (addMatrixEvent) => {
                if (!addMatrixEvent?.getRoomId) {
                    return;
                }
                console.warn(`[m.room.member] ${addMatrixEvent.getStateKey()} joining ${addMatrixEvent.getRoomId()} in matrixChatList`);
                upsertRoomInMatrixChatList(addMatrixEvent.getRoomId());
            };

            /**
             * 
             * triggered from 
             * useDeleteChat::handleChatDeletion() -> MatrixChat.deleteMatrixRoom() @param {} leaveMatrixEvent 
             * @returns 
             */
            const removeToMatrixChatList = (leaveMatrixEvent) => {
                const roomId = leaveMatrixEvent.getRoomId();
                const leavingUserId = leaveMatrixEvent.getStateKey();
                const currentUserId = client.getUserId();
                console.warn(`[m.room.member] ${leavingUserId} leaving ${roomId} in matrixChatList`, currentUserId);

                if (![currentUserId, ADMIN_MATRIX_ID].includes(leavingUserId)) {
                    return;
                }

                const currentMembership = client.getRoom(roomId)?.getMyMembership?.();
                if (leavingUserId === currentUserId && currentMembership === KnownMembership.Join) {
                    console.warn(`[m.room.member] Ignoring stale leave event for rejoined room ${roomId}`);
                    return;
                }

                stopRecoveriesForRoom(roomId);
                closeChatMessageContainerForDeletedRoom(roomId);
                removeRoomInMatrixChatList(roomId);
            };

            const updateRoomDetailsAndChatMessageTimeline = async (event, isLocalEcho) => {
                if (!client.getRoom(event.getRoomId())) {
                    console.warn(`[Matrix] Skipping decryption for event in deleted room: ${event.getRoomId()}`);
                    return;
                }
                try {
                    await client.decryptEventIfNeeded(event);
                } catch (err) {
                    console.warn("[Matrix] Decryption failed:", err);
                    handleDecryptionFailure(event, err);
                }
                // Update the open conversation first. Chat-list hydration can be
                // slower than Matrix delivery and must not hold timeline rendering.
                upsertRoomTimeline(event);
                Promise.resolve(upsertRoomInMatrixChatList(event.getRoomId())).catch(error => {
                    console.warn("[Matrix] Chat-list refresh failed after a timeline update:", error);
                });
                placedRoomToIndexZeroOfMatrixChatList(event.getRoomId());
                
            };

            const closeChatMessageContainerForDeletedRoom = (roomId) => {
                if (openedMatrixChat instanceof MatrixChat && openedMatrixChat.matrixRoomId === roomId) {
                    setOpenedMatrixChat(null);
                }
            };

            addClientListener("event", (event) => {
                console.log("[Event][" + event.getType() + "]");

                if (event.getType() === "m.room.encrypted") {
                    // This is an encrypted message
                    console.warn("Encrypted message received:", event);
                    prepareRoomForReceiving(client.getRoom(event.getRoomId()));
                    updateRoomDetailsAndChatMessageTimeline(event, false);
                    return;

                } else if (event.getType() === "m.room.message") {
                    updateRoomDetailsAndChatMessageTimeline(event, false);
                    return;

                } else if (CALL_EVENT_TYPES.includes(event.getType())) {
                    updateRoomDetailsAndChatMessageTimeline(event, false);
                    return;

                } else if (event.getType() === "m.room.member") {
                    const membership = event.getContent()?.membership;
                    if (membership === KnownMembership.Join) {
                        prepareRoomForReceiving(client.getRoom(event.getRoomId()), true);
                        if (event.getStateKey() === client.getUserId()) {
                            addToMatrixChatList(event);
                        }
                        return;
                    } else if (membership === KnownMembership.Leave) {
                        removeToMatrixChatList(event);
                        return;
                    }

                    return;
                } else if (event.getType() === "m.presence") {
                    //see usePresenceListener::presenceHandler()
                    return;
                } else if (event.getType() === "m.room.redaction") {
                    deleteEventFromRoomDetailsAndChatMessageTimeline(event);
                    return;
                }
            });

            addClientListener(MatrixEventEvent.Decrypted, (event, error) => {
                if (error) {
                    handleDecryptionFailure(event, error);
                    return;
                }

                if (!event?.getRoomId?.()) {
                    return;
                }

                completeKeyRecovery(event);
                updateRoomDetailsAndChatMessageTimeline(event, false);
            });

            const handleDecryptionFailure = (event, error) => {
                if (!event?.getId?.()) {
                    return;
                }
                if (isPermanentlyWithheldRoomKey(error, event)) {
                    stopKeyRecovery(event, MATRIX_KEY_RECOVERY_STATE.WITHHELD);
                    return;
                }
                if (isRetryableMissingRoomKey(error, event)) {
                    void prepareRoomForReceiving(client.getRoom(event.getRoomId()), true);
                    scheduleKeyRecovery(event);
                }
            };

            const scheduleKeyRecovery = (event) => {
                const eventId = event.getId();
                let recovery = decryptionRecoveries.get(eventId);
                if (!recovery) {
                    recovery = { attemptCount: 0, event, request: null, timer: null };
                    decryptionRecoveries.set(eventId, recovery);
                }
                if (recovery.timer) {
                    return;
                }
                if (recovery.attemptCount >= MATRIX_KEY_RECOVERY_RETRY_DELAYS_MS.length) {
                    stopKeyRecovery(event, MATRIX_KEY_RECOVERY_STATE.EXHAUSTED);
                    return;
                }

                setMatrixKeyRecoveryState(event, MATRIX_KEY_RECOVERY_STATE.RECOVERING);
                upsertRoomTimeline(event);
                const retryDelay = MATRIX_KEY_RECOVERY_RETRY_DELAYS_MS[recovery.attemptCount];
                recovery.timer = setTimeout(() => runKeyRecoveryAttempt(event), retryDelay);
            };

            const runKeyRecoveryAttempt = async (event) => {
                const eventId = event.getId();
                const recovery = decryptionRecoveries.get(eventId);
                if (!listenerIsActive || !recovery) {
                    return;
                }
                recovery.timer = null;
                if (!client.getRoom(event.getRoomId())) {
                    stopKeyRecovery(event, null);
                    return;
                }

                recovery.attemptCount += 1;
                try {
                    if (recovery.request) {
                        await cancelRoomKeyRequest(client, recovery.request);
                    }
                    recovery.request = await requestRoomKey(client, event);
                } catch (requestError) {
                    console.warn("[Matrix] Live-device room-key request failed:", requestError);
                }

                const crypto = client.getCrypto();
                if (!crypto) {
                    stopKeyRecovery(event, MATRIX_KEY_RECOVERY_STATE.EXHAUSTED);
                    return;
                }
                try {
                    await crypto.checkKeyBackupAndEnable();
                    if (recovery.attemptCount === 2) {
                        await restoreFullBackupIfAvailable(crypto);
                    }
                    await event.attemptDecryption(crypto, { isRetry: true });
                } catch (retryError) {
                    handleDecryptionFailure(event, retryError);
                    return;
                }

                if (
                    decryptionRecoveries.has(eventId) &&
                    !event.isDecrypted?.() &&
                    !recovery.timer
                ) {
                    scheduleKeyRecovery(event);
                }
            };

            const restoreFullBackupIfAvailable = async (crypto) => {
                if (fullBackupRestorePromise) {
                    return fullBackupRestorePromise;
                }
                const passphrase = SecurePassphraseStorage.get(client);
                fullBackupRestorePromise = (async () => {
                    const backupInfo = await crypto.getKeyBackupInfo();
                    if (!backupInfo?.auth_data) {
                        return false;
                    }
                    if (passphrase) {
                        await crypto.restoreKeyBackupWithPassphrase(passphrase);
                    } else {
                        await crypto.restoreKeyBackup();
                    }
                    return true;
                })().catch((backupError) => {
                    console.warn("[Matrix] Full key backup restoration did not recover the event:", backupError);
                    return false;
                }).then((restored) => {
                    if (!restored) {
                        fullBackupRestorePromise = null;
                    }
                    return restored;
                });
                return fullBackupRestorePromise;
            };

            const completeKeyRecovery = (event) => {
                setMatrixKeyRecoveryState(event, null);
                stopKeyRecovery(event, null);
            };

            const stopKeyRecovery = (event, finalState) => {
                const eventId = event?.getId?.();
                const recovery = eventId ? decryptionRecoveries.get(eventId) : null;
                if (recovery?.timer) {
                    clearTimeout(recovery.timer);
                }
                if (recovery?.request) {
                    cancelRoomKeyRequest(client, recovery.request).catch((cancelError) => {
                        console.warn("[Matrix] Failed to cancel room-key request:", cancelError);
                    });
                }
                if (eventId) {
                    decryptionRecoveries.delete(eventId);
                }
                setMatrixKeyRecoveryState(event, finalState);
                if (finalState && client.getRoom(event.getRoomId())) {
                    upsertRoomTimeline(event);
                }
            };

            const stopRecoveriesForRoom = (roomId) => {
                decryptionRecoveries.forEach((recovery) => {
                    if (recovery.event?.getRoomId?.() === roomId) {
                        stopKeyRecovery(recovery.event, null);
                    }
                });
            };

            retryExistingEncryptedEvents(client, (event, error) => {
                if (listenerIsActive) {
                    handleDecryptionFailure(event, error);
                }
            }).catch(error => {
                console.warn("[Matrix] Background recovery scan failed:", error);
            });

            const deleteEventFromRoomDetailsAndChatMessageTimeline = async (event) => {
                upsertRoomTimeline(event);
                
                if (openedMatrixChat instanceof MatrixChat && openedMatrixChat.matrixRoomId === event.getRoomId()) {
                    openedMatrixChat.getMyRoomLiveDetails();
                }
            };

            addClientListener(RoomEvent.LocalEchoUpdated,
                /**
                 * a confirmation that the message have been delivered to the server
                 * ChatMessages will replace the local echo with this new event.
                 * @param {MatrixEvent} event
                 * @param {Room} room
                 * @param {string} oldEventId
                 */
                (event, room, oldEventId, oldStatus) => {
                    console.log("[Room.localEchoUpdated][" + event.status + "]");
                    
                    if ([EventStatus.SENT, EventStatus.NOT_SENT].includes(event.status)) {
                        updateRoomDetailsAndChatMessageTimeline(event, true);
                    }
                });

            /*
             * Patched method from https://github.com/matrix-org/matrix-js-sdk/blob/develop/src/http-api/index.ts#L53-L156
             *
             * Override the default `client.uploadContent()` method here to customize
             * the file upload behavior — specifically to:
             * - Allow setting a custom upload timeout via `uploadTimeoutMs` (default: 120000ms / 2 minutes)
             */
            client.uploadContent = function(file, opts) {
                const uploadOptions = opts || {};
                const includeFilename = uploadOptions.includeFilename !== undefined
                    ? uploadOptions.includeFilename
                    : true;
                const abortController = uploadOptions.abortController || new AbortController();
                const uploadTimeoutMs = uploadOptions.uploadTimeoutMs || 300000;

                const contentType = uploadOptions.type || (file.type || "application/octet-stream");
                const fileName = uploadOptions.name || file.name;

                const upload = {
                    loaded: 0,
                    total: 0,
                    abortController,
                };

                let resolveUpload, rejectUpload;
                const uploadPromise = new Promise((resolve, reject) => {
                    resolveUpload = resolve;
                    rejectUpload = reject;
                });

                if (window.XMLHttpRequest) {
                    const xhr = new XMLHttpRequest();

                    const timeoutFn = () => {
                        xhr.abort();
                        rejectUpload(new Error("Timeout"));
                    };

                    let timeoutTimer = setTimeout(timeoutFn, uploadTimeoutMs);

                    xhr.onreadystatechange = () => {
                        if (xhr.readyState === XMLHttpRequest.DONE) {
                            clearTimeout(timeoutTimer);
                            try {
                                if (xhr.status === 0) {
                                    if (abortController.signal.aborted) {
                                        rejectUpload(new DOMException("Upload aborted", "AbortError"));
                                    } else {
                                        rejectUpload(new Error("Server closed connection (possibly file too large)"));
                                    }
                                    return;
                                }
                                if (!xhr.responseText) {
                                    rejectUpload(new Error("No response body from server."));
                                    return;
                                }
                                if (xhr.status === 413) {
                                    rejectUpload(new Error("Upload failed: File too large. Max allowed by server."));
                                    return;
                                } else if (xhr.status >= 400) {
                                    let serverMessage = "";
                                    try {
                                        const json = JSON.parse(xhr.responseText);
                                        serverMessage = json.error || json.message || json.errcode || xhr.responseText;
                                    } catch {
                                        serverMessage = xhr.responseText || "Unknown server error";
                                    }
                                    rejectUpload(new Error(`Upload failed: ${xhr.status} ${serverMessage}`));
                                    return;
                                }
                                resolveUpload(JSON.parse(xhr.responseText));

                            } catch (err) {
                                rejectUpload(err);
                            }
                        }
                    };

                    xhr.upload.onprogress = (ev) => {
                        clearTimeout(timeoutTimer);
                        upload.loaded = ev.loaded;
                        upload.total = ev.total;
                        timeoutTimer = setTimeout(timeoutFn, uploadTimeoutMs);
                        if (uploadOptions.progressHandler) {
                            uploadOptions.progressHandler({ loaded: ev.loaded, total: ev.total });
                        }
                    };

                    const url = new URL("/_matrix/media/v3/upload", client.baseUrl);

                    if (includeFilename && fileName) {
                        url.searchParams.set("filename", fileName);
                    }

                    const accessToken = (typeof client.getAccessToken === "function") ? client.getAccessToken() : null;
                    const useAuthHeader = client.opts?.useAuthorizationHeader ?? true;

                    if (!useAuthHeader && accessToken) {
                        url.searchParams.set("access_token", accessToken);
                    }

                    xhr.open("POST", url.href);
                    if (useAuthHeader && accessToken) {
                        xhr.setRequestHeader("Authorization", "Bearer " + accessToken);
                    }
                    xhr.setRequestHeader("Content-Type", contentType);
                    xhr.send(file);

                    abortController.signal.addEventListener("abort", () => {
                        xhr.abort();
                    });
                } else {
                    rejectUpload(new Error("uploadContent requires XMLHttpRequest support"));
                }

                abortController.signal.addEventListener("abort", () => {
                    rejectUpload(new DOMException("Aborted", "AbortError"));
                });

                return uploadPromise;
            };

            const cleanup = () => {
                listenerIsActive = false;
                preparedRoomIds.clear();
                roomPreparationRefreshPending.clear();
                roomPreparationPromises.clear();
                cleanupFns.forEach((fn) => fn());
                decryptionRecoveries.forEach((recovery) => {
                    if (recovery.timer) {
                        clearTimeout(recovery.timer);
                    }
                    if (recovery.request) {
                        cancelRoomKeyRequest(client, recovery.request).catch(() => undefined);
                    }
                });
                decryptionRecoveries.clear();
                if (client.__dailyfixmeChatListenerCleanup === cleanup) {
                    delete client.__dailyfixmeChatListenerCleanup;
                }
            };

            client.__dailyfixmeChatListenerCleanup = cleanup;
            return cleanup;

        } catch (err) {
            console.error("Error in useMatrixClient > runListener", err);
            return () => undefined;
        }
    };

    return { 
        matrixClient,
        isInitialSyncComplete,
        matrixRecoveryRequired,
        chatBackupRecoveryRequired,
        acknowledgeChatBackupRecovery,
        runListener
    };
};

const isUnauthorizedMatrixError = (error) => {
    return error?.httpStatus === 401 ||
        error?.statusCode === 401 ||
        error?.errcode === "M_UNKNOWN_TOKEN" ||
        error?.data?.errcode === "M_UNKNOWN_TOKEN";
};

const isMatrixContinuityFailure = (error) => {
    return error instanceof MatrixContinuityError ||
        error?.response?.data?.errorCode === "CRYPTO_STORE_MISMATCH";
};

const isUnavailableSyncError = (error) => {
    const status = error?.httpStatus ?? error?.statusCode;
    return status === 404 || status === 405 || status === 501;
};

const stopFailedMatrixClient = (clientToStop) => {
    try {
        clientToStop.stopClient?.();
    } catch (error) {
        console.warn("[Matrix] Failed to stop the incompatible Matrix client cleanly:", error);
    }
    clientToStop.removeAllListeners?.();
};

/**
 * @returns {import("matrix-js-sdk/lib/client").MatrixClient}
 */
let client = null;
const createClient = (baseUrl, token, userId, deviceId, cryptoStore, store, fetchFn, disableVoip) => {
    client = sdk.createClient({
        baseUrl,
        fetchFn,
        accessToken: token,
        userId,
        deviceId,
        cryptoStore,
        store,
        disableVoip,
        cryptoCallbacks: {
            getSecretStorageKey: async (opts) => {
                const rustCrypto = await client?.getCrypto();
                const secretStorage = rustCrypto?.secretStorage;
                if (!secretStorage) {
                    console.warn("[getSecretStorageKey] SecretStorage not available");
                    return null;
                }

                const requestedKeys = opts.keys || {};
                const defaultKeyId = await secretStorage.getDefaultKeyId?.();
                const keyId = defaultKeyId && requestedKeys[defaultKeyId]
                    ? defaultKeyId
                    : Object.keys(requestedKeys)[0];
                if (!keyId) {
                    console.warn("[getSecretStorageKey] No requested keys available");
                    return null;
                }

                const keyInfo = requestedKeys[keyId];
                const passphrase = SecurePassphraseStorage.get(client);
                if (!keyInfo?.passphrase || !passphrase) {
                    console.warn("[getSecretStorageKey] Missing passphrase info");
                    return null;
                }
                const { salt, iterations } = keyInfo.passphrase;
                const privateKey = await deriveRecoveryKeyFromPassphrase(passphrase, salt, iterations);
                return [keyId, privateKey];
            }
        }

    });
    if (disableVoip) {
        // MatrixRTC transport discovery starts independently of VoIP support in the SDK.
        client._unstable_getRTCTransports = async () => [];
    }
    return client;
};

const initializeOlm = async () => {
    return bootstrapOlm().then(() => console.log("Olm initialized."))
        .catch(err => console.error("Olm failed to initialize."));
};
